// frontend/packages/business-console/src/components/inbox/ConversationListItem.jsx

import React from 'react';
import { Box, IconButton, Tooltip, Typography } from '@mui/material';
import AttachFileOutlinedIcon from '@mui/icons-material/AttachFileOutlined';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { Link as RouterLink } from 'react-router-dom';
import { getInitials } from '@upryzze/ui';

import { formatRelative } from './relativeTime';
import { useTokens, StatusPill } from '../primitives';
import { INTENT_LABEL, URGENCY_META, INTENT_CHIP } from './triageMeta';

// Tiny inline chip for the triage badges (kept lightweight vs MUI Chip so the
// dense list row stays fast).
function MiniChip({ label, fg, bg }) {
  return (
    <Box
      component="span"
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        px: 0.75,
        height: 18,
        borderRadius: '9px',
        fontSize: 10.5,
        fontWeight: 700,
        lineHeight: 1,
        color: fg,
        bgcolor: bg,
        whiteSpace: 'nowrap',
      }}
    >
      {label}
    </Box>
  );
}

/**
 * One row in the inbox list. Designed for a single column on phones — the
 * row IS the touch target (whole card is a Link). Layout adapts to wider
 * viewports by giving the snippet more room. When `onDelete` is provided
 * (Archived tab only) a small trash icon is rendered at the right edge —
 * clicking it stops link navigation and asks the parent to open the
 * typed-DELETE confirmation modal.
 */
export default function ConversationListItem({
  conversation,
  basePath,
  onDelete,
  onSelect,
  selected = false,
}) {
  const t = useTokens();
  const c = conversation || {};
  const customerName =
    c.customer?.name || c.customer?.email || 'Website Visitor';
  const latest = c.latestMessage || null;
  const snippet = latest?.snippet || '';
  const direction = latest?.direction || 'inbound';
  const ago = formatRelative(c.lastMessageAt || latest?.createdAt);

  const href = `${basePath || ''}/inbox/${c.id}`;
  const unread = Boolean(c.unread);
  const highlight = selected || unread;

  // In split view (desktop) the row selects the thread pane in place; on the
  // mobile/list route it stays a navigating Link. `onSelect` toggles between.
  const rootProps = onSelect
    ? { component: 'div', role: 'button', onClick: () => onSelect(c.id) }
    : { component: RouterLink, to: href };

  return (
    <Box
      {...rootProps}
      sx={{
        display: 'flex',
        alignItems: 'stretch',
        gap: 1.5,
        textDecoration: 'none',
        color: 'inherit',
        py: 1.75,
        px: { xs: 2, md: 2.5 },
        borderBottom: `1px solid ${t.hairline}`,
        transition: 'background-color 0.15s',
        cursor: 'pointer',
        bgcolor: highlight ? t.subtleBlue : 'transparent',
        borderLeft: highlight ? `3px solid ${t.blue}` : '3px solid transparent',
        '&:hover': { backgroundColor: selected ? t.subtleBlue : t.subtle },
      }}
    >
      {/* Avatar initials */}
      <Box
        sx={{
          width: 38,
          height: 38,
          borderRadius: '50%',
          flexShrink: 0,
          display: 'grid',
          placeItems: 'center',
          bgcolor: t.blueBg,
          color: t.blue,
          fontSize: 13,
          fontWeight: 800,
          mt: 0.25,
        }}
      >
        {getInitials(customerName) || '?'}
      </Box>

      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'baseline',
            gap: 1,
            justifyContent: 'space-between',
          }}
        >
          <Typography
            sx={{
              fontSize: 13.5,
              fontWeight: unread ? 800 : 600,
              color: t.text,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {customerName}
          </Typography>
          <Typography sx={{ fontSize: 11.5, color: t.faint, flexShrink: 0 }}>
            {ago}
          </Typography>
        </Box>

        <Typography
          sx={{
            fontSize: 12.5,
            color: t.muted,
            mt: 0.25,
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
          }}
        >
          {direction === 'outbound' ? 'You: ' : ''}
          {snippet || '(no message)'}
        </Typography>

        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            flexWrap: 'wrap',
            gap: 0.75,
            mt: 0.75,
          }}
        >
          {latest?.channel === 'sms' && (
            <StatusPill kind="info" size="sm" label="SMS" />
          )}
          {c.aiTriage?.urgency && URGENCY_META[c.aiTriage.urgency] && (
            <MiniChip
              label={URGENCY_META[c.aiTriage.urgency].label}
              fg={URGENCY_META[c.aiTriage.urgency].fg}
              bg={URGENCY_META[c.aiTriage.urgency].bg}
            />
          )}
          {c.aiTriage?.intent && INTENT_LABEL[c.aiTriage.intent] && (
            <MiniChip
              label={INTENT_LABEL[c.aiTriage.intent]}
              fg={INTENT_CHIP.fg}
              bg={INTENT_CHIP.bg}
            />
          )}
          {Array.isArray(latest?.attachments) &&
            latest.attachments.length > 0 && (
              <Box
                sx={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 0.25,
                  color: 'text.secondary',
                  fontSize: 12,
                }}
              >
                <AttachFileOutlinedIcon sx={{ fontSize: 14 }} />
                {latest.attachments.length}
              </Box>
            )}
        </Box>
      </Box>

      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          color: 'text.disabled',
          flexShrink: 0,
        }}
      >
        {onDelete && (
          <Tooltip title="Delete permanently">
            <IconButton
              size="small"
              onClick={(e) => {
                // The row itself may be a RouterLink — stop both propagation
                // and default navigation so the click only opens the dialog.
                e.preventDefault();
                e.stopPropagation();
                onDelete();
              }}
              aria-label="Delete conversation"
              sx={{ mr: 0.5 }}
            >
              <DeleteOutlineIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
        {/* Unread dot (design), or a chevron affordance on the navigating
            list route. The dot wins so an unread row always reads as unread. */}
        {unread ? (
          <Box
            sx={{
              width: 8,
              height: 8,
              borderRadius: '50%',
              bgcolor: t.blue,
              mt: 0.75,
              alignSelf: 'flex-start',
            }}
          />
        ) : !onSelect && !onDelete ? (
          <ChevronRightIcon />
        ) : null}
      </Box>
    </Box>
  );
}
